import React, { useState, useEffect } from "react"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { useNavigate, useParams } from "react-router-dom"
import {
  Grid,
  Container,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material"
import { useAuthUser, useIsAuthenticated } from "react-auth-kit"
import DeleteIcon from "@mui/icons-material/Delete"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import api from "../../../../utils/api"

function AddQuestionPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const auth = useAuthUser()
  const isAuthenticated = useIsAuthenticated()
  const [selectedType, setSelectedType] = useState("Single")
  const [description, setDescription] = useState("")
  const [username, setUsername] = useState("")
  const [options, setOptions] = useState([
    { option: "", optionNum: 1 },
    { option: "", optionNum: 2 },
  ])
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isSubmitting, setSubmitting] = useState(false)

  useEffect(() => {
    setUsername(isAuthenticated() ? auth()?.username : "")
  }, [isAuthenticated, auth])

  const eventId = auth()?.eventId?._path?.segments[1] ?? "null"

  // all instance ids from eventId
  useEffect(() => {
    if (eventId) {
      api
        .get(`/admin/events/${eventId}/voting-instances-ids`)
        .then((response) => {
          const instances = response.data
          const exists = instances.some((instance: any) => instance.id === id)
          if (!exists) {
            navigate(
              "/not-authorized?message=The%20voting%20instance%20you%20are%20trying%20to%20access%20does%20not%20exist."
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })

      api
        .get(`/admin/instances/${id}/status`)
        .then((response) => {
          const status = response.data.status
          if (status !== "pre-voting") {
            navigate(
              "/not-authorized?message=The%20voting%20instance%20you%20are%20trying%20to%20access%20is%20not%20open%20for%20voting."
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instance status:", error)
        })
    }
  }, [eventId, id, navigate])

  const updateQuestionNum = async () => {
    try {
      const response = await api.get(`/admin/instances/${id}`)
      const instanceData = response.data
      let updatedQuestionNum = instanceData.questionNum
      const oldQuestionNum = instanceData.questionNum
      const QuestionCount = instanceData.questionCount

      if (oldQuestionNum === QuestionCount) {
        setErrorMessage(
          "Maximum Question number has been reached in this Instance."
        )
        setOpenSnackbar(true)
        return false
      } else {
        updatedQuestionNum += 1
      }

      await api.put(`/admin/instances/${id}/count`, {
        questionNum: updatedQuestionNum,
      })

      return true
    } catch (error) {
      console.error("Failed to update question num:", error)
      setErrorMessage("An error occurred while updating the question number.")
      return false
    }
  }

  const handleOptionChange = (index: any, value: any) => {
    const newOptions = [...options]
    newOptions[index].option = value
    setOptions(newOptions)
  }

  const handleSnackbarClose = () => {
    setOpenSnackbar(false)
    setErrorMessage("")
  }

  const handleSubmit = async () => {
    if (!description || options.length < 2) {
      setErrorMessage(
        "All fields are required and you must add at least 2 options."
      )
      setOpenSnackbar(true)
      return
    }

    if (options.some((opt) => !opt.option.trim())) {
      setErrorMessage("All options must have a value.")
      setOpenSnackbar(true)
      return
    }

    const trimmedOptions = options.map((opt) => opt.option.trim())
    const hasDuplicates = new Set(trimmedOptions).size !== trimmedOptions.length

    if (hasDuplicates) {
      setErrorMessage("Options must be unique.")
      setOpenSnackbar(true)
      return
    }

    try {
      if (!(await updateQuestionNum())) {
        return
      }
      setSubmitting(true)
      // Make a single POST request to create both question and options
      await api.post("/admin/questions", {
        username,
        description,
        instanceId: id,
        questionType: selectedType,
        options,
      })

      navigate(`/admin/event-admin/instance/${id}`)
      setSubmitting(false)
    } catch (error) {
      console.error("Error:", error)
      setErrorMessage("An error occurred while creating the question.")
      setOpenSnackbar(true)
      setSubmitting(false)
    }
  }

  const addOption = () => {
    setOptions([...options, { option: "", optionNum: options.length + 1 }])
  }

  const onDragEnd = (result: any) => {
    const { destination, source } = result
    if (!destination) {
      return
    }

    const reorderedOptions = Array.from(options)
    const [removed] = reorderedOptions.splice(source.index, 1)
    reorderedOptions.splice(destination.index, 0, removed)

    // Update the optionNum based on the new order
    reorderedOptions.forEach((opt, index) => {
      opt.optionNum = index + 1
    })

    setOptions(reorderedOptions)
  }

  const removeOption = (index: number) => {
    const newOptions = [...options]
    newOptions.splice(index, 1)

    // Update the optionNum for the remaining options
    newOptions.forEach((opt, index) => {
      opt.optionNum = index + 1
    })

    setOptions(newOptions)
  }

  return (
    <Container component="main">
      <Typography variant="h4" gutterBottom>
        Add a New Question
      </Typography>
      <form onSubmit={(e) => e.preventDefault()}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="What is the question?"
              variant="outlined"
              value={description}
              required
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth margin="normal">
              <InputLabel required id="Question-Type-label">
                Type
              </InputLabel>
              <Select
                label="Type"
                labelId="Question-Type-label"
                id="type-select"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                required
              >
                <MenuItem value="Single">Single</MenuItem>
                <MenuItem value="Ranked">Ranked</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Options:
            </Typography>
            <Button
              onClick={addOption}
              variant="contained"
              color="primary"
              style={{ marginBottom: "15px" }}
            >
              Add Option
            </Button>
          </Grid>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <Grid
                  item
                  xs={12}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {options.map((option, index) => (
                    <Draggable
                      key={option.optionNum}
                      draggableId={String(option.optionNum)}
                      index={option.optionNum - 1}
                    >
                      {(provided, snapshot) => (
                        <Grid
                          item
                          xs={12}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            display: "flex",
                            alignItems: "center",
                            boxShadow: snapshot.isDragging
                              ? "0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)"
                              : "none",
                          }}
                        >
                          <DragIndicatorIcon
                            style={{ cursor: "grab", marginRight: "10px" }}
                          />
                          <TextField
                            fullWidth
                            label={`Option ${option.optionNum}`}
                            variant="outlined"
                            value={option.option}
                            required
                            onChange={(e) =>
                              handleOptionChange(index, e.target.value)
                            }
                            style={{
                              marginBottom: "15px",
                            }}
                          />
                          <DeleteIcon
                            style={{
                              cursor:
                                options.length > 2 ? "pointer" : "not-allowed",
                              marginLeft: "10px",
                              background: options.length > 2 ? "red" : "grey",
                              borderRadius: "25%",
                            }}
                            onClick={() => {
                              if (options.length > 2) {
                                removeOption(index)
                              }
                            }}
                          />
                        </Grid>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Grid>
              )}
            </Droppable>
          </DragDropContext>

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
            <Snackbar
              open={openSnackbar}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity="error"
                sx={{ width: "100%" }}
              >
                {errorMessage}
              </Alert>
            </Snackbar>
          </Grid>
        </Grid>
      </form>
    </Container>
  )
}

export default AddQuestionPage
